// Admin → BC Integration tab.
//
// Squadi auto-login + token management ONLY. Day-to-day mapping work
// (BC competition → BOMS Coaching Program, Sync now, run history) lives
// in its own admin tab — see AdminCompetitionMapping.jsx.
//
// Four blocks here:
//   1. Auto-login credentials (email + password + TOTP seed) — encrypted at rest
//   2. "Login to BC now" button + last-login status
//   3. Manual session token override + organisation key + year id (fallback)
//   4. Test connection (calls a real BC endpoint, reports the upstream result)

import React, { useEffect, useState } from 'react';
import { auditFetch } from '../auditFetch';

const API_BASE = 'https://referee-roster-api-v2-hqghffesdfegd2dq.australiaeast-01.azurewebsites.net/api';

export default function AdminBCIntegration() {
  const [settings, setSettings] = useState(null);
  const [token, setToken] = useState('');
  const [orgKey, setOrgKey] = useState('');
  const [yearId, setYearId] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [totpSeed, setTotpSeed] = useState('');
  const [saving, setSaving] = useState(false);
  const [loggingIn, setLoggingIn] = useState(false);
  const [testing, setTesting] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [authMessage, setAuthMessage] = useState('');
  const [authError, setAuthError] = useState('');
  const [testResult, setTestResult] = useState(null);

  const load = async () => {
    try {
      const res = await fetch(`${API_BASE}/integrations/bc/settings`, { credentials: 'include' });
      if (res.ok) {
        const s = await res.json();
        setSettings(s);
        setOrgKey(s.orgKey || '');
        setYearId(s.yearId || '');
      }
    } catch { /* ignore */ }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true); setError(''); setMessage('');
    try {
      const payload = {};
      if (token.trim()) payload.token = token.trim();
      if (orgKey.trim()) payload.orgKey = orgKey.trim();
      if (yearId.trim()) payload.yearId = yearId.trim();
      if (email.trim()) payload.email = email.trim();
      if (password) payload.password = password;
      if (totpSeed.trim()) payload.totpSeed = totpSeed.trim();

      const res = await auditFetch(`${API_BASE}/integrations/bc/settings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `HTTP ${res.status}`);
      }
      setToken(''); setPassword(''); setTotpSeed('');
      setMessage('Saved.');
      await load();
    } catch (e) { setError(e.message || 'Failed to save'); }
    setSaving(false);
  };

  const clearScope = async (scope, prompt) => {
    if (!window.confirm(prompt)) return;
    setSaving(true);
    await auditFetch(`${API_BASE}/integrations/bc/settings?scope=${scope}`, { method: 'DELETE' });
    await load();
    setSaving(false);
  };

  const loginNow = async () => {
    setLoggingIn(true); setAuthError(''); setAuthMessage('');
    try {
      const res = await auditFetch(`${API_BASE}/integrations/bc/login`, { method: 'POST' });
      const data = await res.json();
      if (!data.ok) throw new Error(data.message || 'Login failed');
      setAuthMessage(`Logged in. New token: ${data.tokenPreview}`);
      await load();
    } catch (e) { setAuthError(e.message || 'Login failed'); }
    setLoggingIn(false);
  };

  const testConnection = async () => {
    setTesting(true); setTestResult(null);
    try {
      const res = await auditFetch(`${API_BASE}/integrations/bc/test`, { method: 'POST' });
      let data = null;
      try { data = await res.json(); } catch { data = null; }
      // Surface non-2xx responses with the body (which may be {message} from the
      // outer catch, or {error, upstreamStatus, upstreamBody} from the inner one).
      if (!res.ok) {
        setTestResult({
          ok: false,
          httpStatus: res.status,
          error: (data && (data.error || data.message)) || `HTTP ${res.status}`,
          upstreamStatus: data && data.upstreamStatus,
          upstreamBody: data && data.upstreamBody,
          orgKey: data && data.orgKey, yearId: data && data.yearId,
        });
      } else {
        // 200 OK — backend may still be reporting ok:false in the body.
        setTestResult({
          ...data,
          // Normalise: if the backend used `message` (legacy 500-catch shape),
          // copy it into `error` so the UI renders it.
          error: data && (data.error || (data.ok === false ? data.message : undefined)),
        });
      }
    } catch (e) {
      setTestResult({ ok: false, error: `Network error: ${e.message}` });
    }
    setTesting(false);
  };

  const hasCredentials =
    settings?.auth?.hasEmail && settings?.auth?.hasPassword && settings?.auth?.hasTotpSeed;

  // (Mapping + run-history state, handlers, and JSX now live in
  //  AdminCompetitionMapping.jsx — keep this page focused on auth.)

  // Lightweight inline styles — match the rest of Admin.
  const card = { background: 'white', border: '1px solid #e0e0e0', borderRadius: 8, padding: '1.25rem 1.5rem', marginBottom: '1rem' };
  const cardH = { margin: '0 0 0.75rem 0', fontSize: '1.05rem', color: '#333' };
  const helpP = { color: '#666', fontSize: '0.85rem', margin: '0 0 0.75rem 0', lineHeight: 1.5 };
  const fieldRow = { marginBottom: '0.75rem' };
  const fieldLbl = { display: 'block', fontSize: '0.85rem', fontWeight: 600, color: '#444', marginBottom: '0.25rem' };
  const fieldHint = { color: '#888', fontSize: '0.75rem', marginTop: '0.2rem' };
  const fieldInput = { width: '100%', padding: '0.5rem', border: '1px solid #ccc', borderRadius: 4, fontSize: '0.9rem', boxSizing: 'border-box' };
  const btnRow = { display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.5rem' };
  const btnPrimary = { padding: '0.5rem 1rem', background: '#1565c0', color: 'white', border: '1px solid #0d47a1', borderRadius: 4, cursor: 'pointer', fontWeight: 600 };
  const btnSuccess = { ...btnPrimary, background: '#2e7d32', borderColor: '#1b5e20' };
  const btnGhost = { padding: '0.5rem 1rem', background: 'white', color: '#c62828', border: '1px solid #c62828', borderRadius: 4, cursor: 'pointer', fontWeight: 500 };

  return (
    <div className="admin-section">
      <h2 style={{ marginTop: 0 }}>Basketball Connect Integration</h2>
      <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: '1.25rem' }}>
        Automated daily sync of Coaching Program registrations from Basketball Connect (Squadi).
        Stores admin credentials encrypted at rest and re-logins automatically when the session
        token expires. Stage 1 (this page) handles authentication. Mapping &amp; sync of
        competitions follows once the auth is verified.
      </p>

      {/* ---------- CREDENTIALS ---------- */}
      <div style={card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={cardH}>Auto-login credentials</h3>
          {hasCredentials && <span style={{ fontSize: '0.75rem', color: '#2e7d32', fontWeight: 600 }}>configured</span>}
        </div>
        <p style={helpP}>
          The Basketball Connect admin email, password and TOTP seed (the secret behind your
          authenticator-app QR). The server logs in on demand and re-logins automatically when
          a call returns 401/403 — no more manual token pasting.
        </p>

        <div style={fieldRow}>
          <label style={fieldLbl}>
            Email{' '}
            {settings?.auth?.emailPreview && (
              <span style={{ fontWeight: 'normal', color: '#888', fontSize: '0.78rem' }}>
                (current: {settings.auth.emailPreview})
              </span>
            )}
          </label>
          <input
            type="email" value={email} onChange={e => setEmail(e.target.value)}
            placeholder="you@example.com" style={fieldInput} autoComplete="off"
          />
        </div>

        <div style={fieldRow}>
          <label style={fieldLbl}>
            Password{' '}
            {settings?.auth?.hasPassword && (
              <span style={{ fontWeight: 'normal', color: '#888', fontSize: '0.78rem' }}>(currently set)</span>
            )}
          </label>
          <input
            type="password" value={password} onChange={e => setPassword(e.target.value)}
            placeholder={settings?.auth?.hasPassword ? '•••••• (leave blank to keep)' : 'Enter password'}
            style={fieldInput} autoComplete="new-password"
          />
        </div>

        <div style={fieldRow}>
          <label style={fieldLbl}>
            TOTP seed (base32){' '}
            {settings?.auth?.hasTotpSeed && (
              <span style={{ fontWeight: 'normal', color: '#888', fontSize: '0.78rem' }}>(currently set)</span>
            )}
          </label>
          <input
            type="password" value={totpSeed} onChange={e => setTotpSeed(e.target.value)}
            placeholder={settings?.auth?.hasTotpSeed ? '•••••• (leave blank to keep)' : 'e.g. GRIWMZZYJM'}
            style={{ ...fieldInput, fontFamily: 'monospace' }} autoComplete="off"
          />
          <p style={fieldHint}>
            The base32 secret behind BC's MFA enrolment QR. Decode the QR with any QR reader
            and copy the <code>secret=</code> value from the <code>otpauth://</code> URL.
          </p>
        </div>

        <div style={btnRow}>
          <button onClick={save} disabled={saving} style={btnPrimary}>
            {saving ? 'Saving…' : 'Save credentials'}
          </button>
          <button
            onClick={loginNow}
            disabled={loggingIn || !hasCredentials}
            style={hasCredentials ? btnSuccess : { ...btnSuccess, opacity: 0.5, cursor: 'not-allowed' }}
            title={!hasCredentials ? 'Save credentials first' : 'Login to BC now'}
          >
            {loggingIn ? 'Logging in…' : 'Login to BC now'}
          </button>
          {hasCredentials && (
            <button onClick={() => clearScope('credentials', 'Clear stored BC credentials? Auto-login will stop until you re-enter them.')} disabled={saving} style={btnGhost}>
              Clear credentials
            </button>
          )}
          {authMessage && <span style={{ color: '#2e7d32', fontSize: '0.85rem', marginLeft: 'auto' }}>{authMessage}</span>}
          {authError && <span style={{ color: '#c62828', fontSize: '0.85rem', marginLeft: 'auto' }}>{authError}</span>}
        </div>

        {settings?.auth?.lastAutoLogin && (
          <div style={{ marginTop: '0.8rem', paddingTop: '0.6rem', borderTop: '1px solid #eee', fontSize: '0.78rem', color: '#666' }}>
            Last auto-login: {new Date(settings.auth.lastAutoLogin).toLocaleString()} · {settings.auth.lastAutoLoginStatus}
          </div>
        )}
      </div>

      {/* ---------- TOKEN OVERRIDE + ORG/YEAR ---------- */}
      <div style={card}>
        <h3 style={cardH}>Session token + organisation</h3>
        <p style={helpP}>
          The current BC session token is set automatically by Login above. Only paste here if
          auto-login is broken or you need to override. Organisation Key + Year ID identify
          which IBA competitions to pull.
        </p>

        <div style={fieldRow}>
          <label style={fieldLbl}>
            Current token: {settings?.hasToken ? settings.tokenPreview : 'not set'}
          </label>
          <input
            type="password" value={token} onChange={e => setToken(e.target.value)}
            placeholder="(optional) paste hex token to override"
            style={{ ...fieldInput, fontFamily: 'monospace', fontSize: '0.78rem' }} autoComplete="off"
          />
        </div>

        <div style={fieldRow}>
          <label style={fieldLbl}>Organisation Unique Key</label>
          <input
            type="text" value={orgKey} onChange={e => setOrgKey(e.target.value)}
            placeholder="8981f6a9-9511-41c1-bdd1-453f24c86ede"
            style={{ ...fieldInput, fontFamily: 'monospace', fontSize: '0.78rem' }}
          />
          <p style={fieldHint}>Default is Illawarra Basketball.</p>
        </div>

        <div style={fieldRow}>
          <label style={fieldLbl}>Year ID</label>
          <input
            type="number" value={yearId} onChange={e => setYearId(e.target.value)}
            placeholder="8" style={{ ...fieldInput, width: 100, fontFamily: 'monospace' }}
          />
          <p style={fieldHint}>For Illawarra, 8 = 2026.</p>
        </div>

        <div style={btnRow}>
          <button onClick={save} disabled={saving} style={btnPrimary}>
            {saving ? 'Saving…' : 'Save'}
          </button>
          {settings?.hasToken && (
            <button onClick={() => clearScope('token', 'Clear the BC session token? Auto-login will fetch a new one on next call.')} disabled={saving} style={btnGhost}>
              Clear token
            </button>
          )}
          <button onClick={testConnection} disabled={testing || !settings?.hasToken} style={{ ...btnPrimary, background: '#5e35b1', borderColor: '#4527a0' }}>
            {testing ? 'Testing…' : 'Test connection'}
          </button>
          {message && <span style={{ color: '#2e7d32', fontSize: '0.85rem' }}>{message}</span>}
          {error && <span style={{ color: '#c62828', fontSize: '0.85rem' }}>{error}</span>}
        </div>

        {testResult && (
          <div style={{ marginTop: '0.75rem', padding: '0.6rem 0.8rem', background: testResult.ok ? '#e8f5e9' : '#fff8e1', border: '1px solid ' + (testResult.ok ? '#a5d6a7' : '#ffe082'), borderRadius: 4, fontSize: '0.8rem' }}>
            <div><strong>{testResult.ok ? 'Connection OK' : 'Connection failed'}</strong>{testResult.note ? ` — ${testResult.note}` : ''}</div>
            {(testResult.orgKey || testResult.yearId) && (
              <div style={{ marginTop: 4, color: '#555' }}>
                org: <code>{testResult.orgKey}</code> · yearRefId: <code>{testResult.yearId}</code>
                {testResult.competitionCount != null && <> · competitions: <strong>{testResult.competitionCount}</strong></>}
                {testResult.totalRows != null && <> · fee rows: {testResult.totalRows}</>}
              </div>
            )}
            {testResult.error && (
              <div style={{ marginTop: 6, color: '#c62828', whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '0.75rem' }}>
                {testResult.error}
              </div>
            )}
            {testResult.httpStatus && (
              <div style={{ marginTop: 6, color: '#666', fontSize: '0.75rem' }}>
                SwishIQ HTTP: <code>{testResult.httpStatus}</code>
              </div>
            )}
            {testResult.upstreamStatus && (
              <div style={{ marginTop: 6, color: '#666', fontSize: '0.75rem' }}>
                Upstream status: <code>{testResult.upstreamStatus}</code>
                {testResult.upstreamBody && <> · body: <code>{String(testResult.upstreamBody).slice(0, 200)}</code></>}
              </div>
            )}
            {testResult.sampleItem && (
              <div style={{ marginTop: 6, color: '#555', fontSize: '0.75rem' }}>
                <div>Sample competition:</div>
                <div style={{ marginLeft: 8 }}>
                  <code>{testResult.sampleItem.competitionName}</code>
                  {testResult.sampleItem.competitionId != null && <> (id {testResult.sampleItem.competitionId})</>}
                </div>
              </div>
            )}
            {/* Legacy shape (multiple probe results) — keep for back-compat */}
            {Array.isArray(testResult.results) && (
              <ul style={{ margin: '0.4rem 0 0 1rem', padding: 0 }}>
                {testResult.results.map((r, i) => (
                  <li key={i} style={{ color: r.ok ? '#2e7d32' : '#c62828' }}>
                    <code>{r.path}</code> — {r.ok ? 'OK' : (r.error || 'failed')}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      {/* Mapping + run history sections moved to Admin → BC Registrations. */}
    </div>
  );
}
